import axios, { ApiResponse, AppError } from "@/api/client";
import { PaginationParams, PaginationResponse } from "@/types";
import { cursorFromNextUrl } from "@/types/pagination";

/**
 * Utility type to extract the element type from any array property of an object.
 *
 * For example, if T is { models: ExistingModel[] | null }, ArrayElement<T> will be ExistingModel.
 */
type ArrayElement<T> = T extends { [K: string]: (infer U)[] | null } ? U : never;

/**
 * Generic paginated fetch for a resource.
 * @template T - The element type of the resource array (e.g., ExistingModel).
 * @template R - The API response data type, which must have a property that is an array of T under the 'data' field (e.g., { models: ExistingModel[] | null }).
 * @param url - API endpoint.
 * @param paginationParams - Pagination parameters.
 * @param responseDataExtractor - Function to extract the array of resources from the API response (e.g., response => response.data.data.models ?? []).
 * @param errorMsg - Error message for failures.
 * @param additionalParams - Additional query parameters to append to the request (e.g., { recipient_id: "123" }).
 * @returns A promise resolving to a paginated response of the resource array.
 */
export async function getResourcePage<T, R extends { [key: string]: T[] | null }>(
  url: string,
  paginationParams: PaginationParams,
  responseDataExtractor: (responseData: ApiResponse<R>) => ArrayElement<R>[],
  errorMsg: string = "Failed to fetch data.",
  additionalParams: Record<string, string | number> = {},
): Promise<PaginationResponse<ArrayElement<R>>> {
  const params = new URLSearchParams();
  if (paginationParams.page_size !== undefined) {
    params.append("page_size", `${paginationParams.page_size}`);
  }
  if (paginationParams.cursor) {
    params.append("cursor", paginationParams.cursor);
  }
  for (const [key, value] of Object.entries(additionalParams)) {
    params.append(key, `${value}`);
  }

  try {
    const response: ApiResponse<R> = await axios.get(url, { params });

    return {
      data: responseDataExtractor(response),
      next_url: response.data.next_url,
      has_next: response.data.has_next || false,
    };
  } catch (reason: any) {
    const e: AppError = {
      error: {
        message: reason.response?.data?.message || errorMsg,
        suppressGlobalNotification: false,
        statusCode: reason.response?.status,
      },
    };
    throw e;
  }
}

export async function getAllPages<T>(
  getPage: (params: Pick<PaginationParams, "cursor" | "page_size">) => Promise<PaginationResponse<T>>,
  pageSize?: number,
): Promise<T[]> {
  let response = await getPage({ page_size: pageSize });
  let all: T[] = [...(response.data ?? [])];

  while (response.has_next) {
    const nextCursor = cursorFromNextUrl(response.next_url);

    if (!nextCursor) {
      throw new Error("Pagination stopped early: response reported more pages but returned no cursor.");
    }

    response = await getPage({ cursor: nextCursor, page_size: pageSize });
    all = [...all, ...(response.data ?? [])];
  }

  return all;
}

/**
 * Fetches a single resource from an API endpoint.
 * @template T - The type of the resource to extract.
 * @template R - The API response data type, which must have a property of type T under the 'data' field.
 * @param url - API endpoint.
 * @param resourceExtractor - Function to extract the resource from the API response (e.g., response => response.data.data.model).
 * @param errorMsg - Error message for failures.
 * @returns A promise resolving to the resource.
 */
export async function getResource<T, R extends { [key: string]: T }>(
  url: string,
  resourceExtractor: (responseData: ApiResponse<R>) => T,
  errorMsg: string,
): Promise<T> {
  try {
    const response: ApiResponse<R> = await axios.get(url, {});
    return resourceExtractor(response);
  } catch (reason: any) {
    throw {
      error: {
        message: reason?.response?.data?.message || errorMsg,
        suppressGlobalNotification: true,
        statusCode: reason?.response?.status,
      },
    };
  }
}

/**
 * Creates a standardized error handler for API requests.
 * @param defaultMessage - The default error message to use if the API doesn't provide one.
 * @param suppressGlobalNotification - Whether to suppress global error notifications (default: false).
 * @returns A function that throws a standardized AppError.
 */
export const handleApiError =
  (defaultMessage: string, suppressGlobalNotification = false) =>
  (reason: any): never => {
    throw {
      error: {
        message: reason?.response?.data?.message || defaultMessage,
        suppressGlobalNotification,
        statusCode: reason?.response?.status,
      },
    };
  };

/**
 * Extracts error message from TanStack Query mutation errors.
 * Handles both AppError format (from our API helpers) and standard Error objects.
 * @param error - The error object from a TanStack Query mutation.
 * @param fallback - Fallback message if no error message can be extracted.
 * @returns The extracted error message.
 */
export function getErrorMessage(error: unknown, fallback = "An error occurred"): string {
  if (!error) {
    return fallback;
  }

  // Check for our AppError format: { error: { message: string } }
  if (typeof error === "object" && "error" in error) {
    const appError = error as AppError;
    if (appError.error?.message) {
      return appError.error.message;
    }
  }

  // Check for standard Error object
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

/**
 * Creates a resource via POST request.
 * @template TPayload - The type of the payload to send.
 * @template TResponse - The type of the resource in the response.
 * @param url - The API endpoint URL.
 * @param payload - The data to send in the request.
 * @param wrapperKey - The key that wraps the payload in the request body (e.g., "source", "destination").
 * @param responseKey - The key that contains the resource in the response data (e.g., "source", "destination").
 * @param errorMessage - Error message for failures.
 * @param suppressGlobalNotification - Whether to suppress global error notifications (default: false).
 * @returns A promise resolving to the created resource.
 */
export async function createResource<TPayload, TResponse>(
  url: string,
  payload: TPayload,
  wrapperKey: string,
  responseKey: string,
  errorMessage: string,
  suppressGlobalNotification = false,
): Promise<TResponse> {
  return axios
    .post(url, { [wrapperKey]: payload })
    .then((response: ApiResponse<{ [key: string]: TResponse }>) => response.data.data[responseKey])
    .catch(handleApiError(errorMessage, suppressGlobalNotification));
}

/**
 * Updates a resource via PATCH request.
 * @template TPayload - The type of the payload to send.
 * @template TResponse - The type of the resource in the response.
 * @param url - The API endpoint URL (should include the resource ID).
 * @param payload - The partial data to update.
 * @param wrapperKey - The key that wraps the payload in the request body.
 * @param responseKey - The key that contains the resource in the response data.
 * @param errorMessage - Error message for failures.
 * @param suppressGlobalNotification - Whether to suppress global error notifications (default: false).
 * @returns A promise resolving to the updated resource.
 */
export async function updateResource<TPayload, TResponse>(
  url: string,
  payload: Partial<TPayload>,
  wrapperKey: string,
  responseKey: string,
  errorMessage: string,
  suppressGlobalNotification = false,
): Promise<TResponse> {
  return axios
    .patch(url, { [wrapperKey]: payload })
    .then((response: ApiResponse<{ [key: string]: TResponse }>) => response.data.data[responseKey])
    .catch(handleApiError(errorMessage, suppressGlobalNotification));
}

/**
 * Deletes a resource via DELETE request.
 * @param url - The API endpoint URL (should include the resource ID).
 * @param errorMessage - Error message for failures.
 * @param suppressGlobalNotification - Whether to suppress global error notifications (default: false).
 * @returns A promise resolving to void.
 */
export async function deleteResource(
  url: string,
  errorMessage: string,
  suppressGlobalNotification = false,
): Promise<void> {
  return axios
    .delete(url)
    .then(() => undefined)
    .catch(handleApiError(errorMessage, suppressGlobalNotification));
}
